import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';
import { getProgressionSkillsForSport } from '../data/progression/skills';
import { buildInstructorLessonPlan } from '../utils/instructorLessonBuilder';
import { buildInstructorQuickLessonPath } from '../utils/instructorQuickLessonPath';
import { normalizeLessonInstanceRecord } from '../utils/lessonInstancePersistence';
import { saveQuickLessonLog, quickLogHasEvidence } from '../utils/quickLessonLogWriter';
import { buildStudentProgressSummary } from '../utils/progressTracking';
import {
  applyQuickLessonLogToLessonInstance,
  inferFurthestStageReached,
  inferQuickOutcomeChoice,
  QUICK_LESSON_OUTCOME_OPTIONS,
} from '../utils/quickLessonLog';
import { normalizeSportName } from '../utils/drillTemplates';

const SPORT_OPTIONS = [
  { key: 'skiing', label: 'Skiing' },
  { key: 'snowboarding', label: 'Snowboarding' },
];

function createDocRecord(docSnap) {
  return { id: docSnap.id, ...docSnap.data() };
}

function formatLabel(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function getDominantSport(videos = []) {
  const counts = new Map();
  (videos || []).forEach((video) => {
    const sport = normalizeSportName(video?.sport || '');
    if (!sport) return;
    counts.set(sport, (counts.get(sport) || 0) + 1);
  });

  return Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0] || 'skiing';
}

function InstructorQuickLessonLogPage() {
  const { currentUser, userData } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const queryVideoId = String(params.get('videoId') || '').trim();
  const queryStudentId = String(params.get('studentId') || '').trim();
  const querySport = normalizeSportName(params.get('sport') || '');
  const querySkillId = String(params.get('skill') || '').trim();
  const queryLessonId = String(params.get('lessonId') || '').trim();

  const [loading, setLoading] = useState(true);
  const [studentId, setStudentId] = useState(queryStudentId);
  const [studentName, setStudentName] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [videos, setVideos] = useState([]);
  const [feedbackMap, setFeedbackMap] = useState({});
  const [selectedSport, setSelectedSport] = useState(querySport || '');
  const [selectedSkillId, setSelectedSkillId] = useState(querySkillId || '');
  const [lessonInstanceId, setLessonInstanceId] = useState(queryLessonId);
  const [lessonPlan, setLessonPlan] = useState(null);
  const [furthestStageReached, setFurthestStageReached] = useState('review');
  const [outcomeChoice, setOutcomeChoice] = useState('in_progress');
  const [quickNote, setQuickNote] = useState('');
  const [recommendedNextSkillId, setRecommendedNextSkillId] = useState('');
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchContext = async () => {
      if (!currentUser?.uid) return;

      setLoading(true);
      setLoadError('');
      try {
        let baseVideo = null;
        let resolvedStudentId = queryStudentId;
        let resolvedSport = querySport;
        let loadedLesson = null;

        if (queryLessonId) {
          const lessonSnap = await getDoc(doc(db, 'lessonInstances', queryLessonId));
          if (lessonSnap.exists()) {
            loadedLesson = normalizeLessonInstanceRecord(lessonSnap, queryLessonId);
            resolvedStudentId = resolvedStudentId || loadedLesson.studentId || '';
            resolvedSport = resolvedSport || normalizeSportName(loadedLesson.sport || '');
            setLessonInstanceId(loadedLesson.lessonId);
            setSelectedSkillId(loadedLesson.targetSkillId || querySkillId || '');
            setStudentName(String(loadedLesson.studentName || '').trim());
            setStudentEmail(String(loadedLesson.studentEmail || '').trim());
          } else {
            setLessonInstanceId('');
            setLoadError('We could not find that saved lesson log. A new quick log has been opened instead.');
          }
        }

        if (queryVideoId) {
          const videoSnap = await getDoc(doc(db, 'videos', queryVideoId));
          if (videoSnap.exists()) {
            baseVideo = createDocRecord(videoSnap);
            resolvedStudentId = resolvedStudentId || baseVideo.userId || baseVideo.studentId || '';
            resolvedSport = resolvedSport || normalizeSportName(baseVideo.sport || '');
          }
        }

        const videoResults = [];
        const seenIds = new Set();
        const appendDocs = (snapshot) => {
          (snapshot?.docs || []).forEach((docSnap) => {
            if (seenIds.has(docSnap.id)) return;
            seenIds.add(docSnap.id);
            videoResults.push(createDocRecord(docSnap));
          });
        };

        if (resolvedStudentId) {
          appendDocs(await getDocs(query(collection(db, 'videos'), where('userId', '==', resolvedStudentId))));
          appendDocs(await getDocs(query(collection(db, 'videos'), where('studentId', '==', resolvedStudentId))));

          const userSnap = await getDoc(doc(db, 'users', resolvedStudentId));
          if (userSnap.exists()) {
            setStudentName((current) => current || String(userSnap.data()?.name || '').trim());
            setStudentEmail((current) => current || String(userSnap.data()?.email || '').trim());
          } else {
            setStudentName((current) => current || '');
            setStudentEmail((current) => current || '');
          }
        }

        if (baseVideo && !seenIds.has(baseVideo.id)) {
          videoResults.push(baseVideo);
        }

        const sortedVideos = videoResults.sort((a, b) => {
          const aTime = a.reviewedAt?.seconds || a.createdAt?.seconds || 0;
          const bTime = b.reviewedAt?.seconds || b.createdAt?.seconds || 0;
          return bTime - aTime;
        });

        const nextFeedbackMap = {};
        await Promise.all(
          sortedVideos.map(async (video) => {
            if (video.status !== 'reviewed') return;
            const feedbackSnap = await getDocs(collection(db, 'videos', video.id, 'timestampedFeedback'));
            nextFeedbackMap[video.id] = (feedbackSnap?.docs || [])
              .map((feedbackDoc) => feedbackDoc.data())
              .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
          })
        );

        setStudentId(resolvedStudentId);
        setVideos(sortedVideos);
        setFeedbackMap(nextFeedbackMap);
        setSelectedSport((current) => current || resolvedSport || getDominantSport(sortedVideos));
        if (loadedLesson) {
          setQuickNote(loadedLesson.lessonOutcome?.instructorSummary || '');
          setRecommendedNextSkillId(loadedLesson.lessonOutcome?.recommendedNextSkillId || '');
          setOutcomeChoice(inferQuickOutcomeChoice(loadedLesson));
          setFurthestStageReached(inferFurthestStageReached(loadedLesson.stages));
          setLessonPlan(loadedLesson);
        }
      } catch (error) {
        console.error('Error loading quick lesson log:', error);
        setLoadError('The quick lesson log could not load this lesson context safely.');
      } finally {
        setLoading(false);
      }
    };

    fetchContext();
  }, [currentUser?.uid, queryLessonId, querySkillId, querySport, queryStudentId, queryVideoId]);

  const sportVideos = useMemo(
    () => videos.filter((video) => normalizeSportName(video?.sport || '') === selectedSport),
    [selectedSport, videos]
  );
  const summary = useMemo(
    () => buildStudentProgressSummary(sportVideos, { feedbackMap }),
    [feedbackMap, sportVideos]
  );
  const availableSkills = useMemo(() => getProgressionSkillsForSport(selectedSport), [selectedSport]);

  useEffect(() => {
        setLessonPlan((current) =>
      buildInstructorLessonPlan({
        videoId: queryVideoId,
        studentId,
        instructorId: currentUser?.uid || '',
        studentName,
        studentEmail,
        sport: selectedSport,
        targetSkillId: selectedSkillId,
        summary,
        existingLesson:
          current?.sport === selectedSport && current?.targetSkillId === selectedSkillId
            ? current
            : null,
      })
    );
  }, [currentUser?.uid, queryVideoId, selectedSkillId, selectedSport, studentEmail, studentId, studentName, summary]);

  useEffect(() => {
    if (!selectedSport || availableSkills.length === 0) return;
    const recommendedSkillId = buildInstructorLessonPlan({
      videoId: queryVideoId,
      studentId,
      instructorId: currentUser?.uid || '',
      studentName,
      studentEmail,
      sport: selectedSport,
      summary,
    }).targetSkillId;

    if (!selectedSkillId || !availableSkills.some((skill) => skill.skillId === selectedSkillId)) {
      setSelectedSkillId((querySkillId && availableSkills.some((skill) => skill.skillId === querySkillId) && querySkillId) || recommendedSkillId || availableSkills[0].skillId);
    }
  }, [availableSkills, currentUser?.uid, querySkillId, queryVideoId, selectedSkillId, selectedSport, studentEmail, studentId, studentName, summary]);

  useEffect(() => {
    if (!selectedSport) return;
    const nextPath = buildInstructorQuickLessonPath({
      videoId: queryVideoId,
      studentId,
      sport: selectedSport,
      skill: selectedSkillId,
      lessonId: lessonInstanceId,
    });
    const currentPath = `${location.pathname}${location.search}`;
    if (currentPath !== nextPath) {
      navigate(nextPath, { replace: true });
    }
  }, [lessonInstanceId, location.pathname, location.search, navigate, queryVideoId, selectedSkillId, selectedSport, studentId]);

  const quickPreview = useMemo(
    () =>
      applyQuickLessonLogToLessonInstance(lessonPlan || {}, {
        furthestStageReached,
        outcomeChoice,
        quickNote,
        recommendedNextSkillId,
      }),
    [furthestStageReached, lessonPlan, outcomeChoice, quickNote, recommendedNextSkillId]
  );

  const nextSkillOptions = availableSkills;

  // Pass 68 — a per-draft standalone lesson id, generated ONCE and reused, so a manual (no-booking) Quick Log
  // is idempotent across double-tap / retry (get-or-create on the same deterministic id, never addDoc-per-save).
  const standaloneIdRef = useRef('');
  const delivery = {
    furthestStageReached, outcomeChoice, quickNote, recommendedNextSkillId,
    sport: selectedSport, targetSkillId: selectedSkillId,
  };
  // §61 minimum evidence — Complete is disabled until the log carries real delivery content.
  const hasEvidence = quickLogHasEvidence(delivery);

  const persistQuickLog = async (status) => {
    if (saving || !currentUser?.uid) return;
    if (status === 'completed' && !hasEvidence) { setSaveError('Add a note, focus, or outcome before completing the lesson.'); return; }
    setSaving(true); setSaveError(''); setSaveMessage('');
    try {
      // organizationId + the actor's canonical business instructor id come from the loaded lesson (booking-
      // linked) or the actor's membership (standalone) — NEVER the Auth uid used as instructorId.
      const org = lessonPlan?.organizationId || (userData?.organizationMemberships || [])[0]?.organizationId || userData?.orgId || '';
      const memberId = (userData?.organizationMemberships || []).find((m) => m.organizationId === org)?.userId || '';
      // booking-linked / already-saved → the existing id; standalone → a stable generated id (once).
      let targetId = lessonInstanceId;
      const isStandalone = !targetId;
      if (isStandalone) {
        if (!standaloneIdRef.current) standaloneIdRef.current = doc(collection(db, 'lessonInstances')).id;
        targetId = standaloneIdRef.current;
      }
      const res = await saveQuickLessonLog(db, {
        lessonInstanceId: targetId,
        delivery,
        status,
        seed: isStandalone ? { organizationId: org, instructorId: memberId, studentId, studentName, studentEmail, sport: selectedSport, targetSkillId: selectedSkillId } : null,
        userData, actorAuthUserId: currentUser.uid, actorInstructorId: memberId, organizationId: org,
      });
      setLessonInstanceId(res.lessonInstanceId);
      setLessonPlan((cur) => ({ ...(cur || {}), ...quickPreview, status }));
      setSaveMessage(status === 'completed' ? 'Lesson log saved as completed.' : 'Lesson log saved as a draft.');
    } catch (error) {
      console.error('Error saving quick lesson log:', error);
      setSaveError(error?.message || 'We could not save this quick lesson log right now. Your edits are still on screen.');
    } finally {
      setSaving(false);
    }
  };

  const recommendedSkillName = summary?.nextSkill?.name || summary?.currentSkill?.name || lessonPlan?.targetSkillName || 'No recommendation yet';

  return (
    <div className="mx-auto max-w-4xl p-4">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Quick Lesson Log</p>
        <h1 className="mt-2 text-2xl font-semibold text-slate-950">Log The Essence Of The Lesson</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">
          Record the skill, the furthest stage reached, how the lesson went, and what should happen next. The app will translate that into the full lesson-instance structure for you.
        </p>

        {loadError ? (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {loadError}
          </div>
        ) : null}
        {saveMessage ? (
          <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            {saveMessage}
          </div>
        ) : null}
        {saveError ? (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
            {saveError}
          </div>
        ) : null}

        {loading ? (
          <p className="mt-6 text-sm text-slate-500">Loading quick lesson log...</p>
        ) : (
          <>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Student</p>
                <p className="mt-2 text-sm font-medium text-slate-900">{studentName || studentEmail || studentId || 'Unassigned client'}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recommended Skill</p>
                <p className="mt-2 text-sm font-medium text-slate-900">{recommendedSkillName}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Current Level</p>
                <p className="mt-2 text-sm font-medium text-slate-900">{summary?.currentLevel || 'beginner'}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Track</p>
                <p className="mt-2 text-sm font-medium text-slate-900">{lessonPlan?.trackName || 'Core Progression'}</p>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              {SPORT_OPTIONS.map((sport) => {
                const isActive = selectedSport === sport.key;
                return (
                  <button
                    key={sport.key}
                    type="button"
                    onClick={() => {
                      setSelectedSport(sport.key);
                      setSelectedSkillId('');
                    }}
                    className={`rounded-xl border px-4 py-2 text-sm font-medium ${
                      isActive
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {sport.label}
                  </button>
                );
              })}
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="quick-student-name">
                  Student Name
                </label>
                <input
                  id="quick-student-name"
                  value={studentName}
                  onChange={(event) => setStudentName(event.target.value)}
                  placeholder="Optional if the school is creating this lesson before the student joins"
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-900"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="quick-student-email">
                  Student Email
                </label>
                <input
                  id="quick-student-email"
                  type="email"
                  value={studentEmail}
                  onChange={(event) => setStudentEmail(event.target.value)}
                  placeholder="Optional email for a future client access or invite flow"
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-900"
                />
              </div>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="quick-target-skill">
                  What skill did you work on?
                </label>
                <select
                  id="quick-target-skill"
                  value={selectedSkillId}
                  onChange={(event) => setSelectedSkillId(event.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-900"
                >
                  {availableSkills.map((skill) => (
                    <option key={skill.skillId} value={skill.skillId}>
                      {skill.name} ({formatLabel(skill.level)})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="quick-furthest-stage">
                  How far did the student get?
                </label>
                <select
                  id="quick-furthest-stage"
                  value={furthestStageReached}
                  onChange={(event) => setFurthestStageReached(event.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-900"
                >
                  {(lessonPlan?.stages || []).map((stage) => (
                    <option key={stage.stage} value={stage.stage}>
                      {stage.title || formatLabel(stage.stage)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="quick-outcome-choice">
                  How did the lesson go?
                </label>
                <select
                  id="quick-outcome-choice"
                  value={outcomeChoice}
                  onChange={(event) => setOutcomeChoice(event.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-900"
                >
                  {QUICK_LESSON_OUTCOME_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {formatLabel(option)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="quick-next-skill">
                  Optional: what should happen next?
                </label>
                <select
                  id="quick-next-skill"
                  value={recommendedNextSkillId}
                  onChange={(event) => setRecommendedNextSkillId(event.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-900"
                >
                  <option value="">Keep current recommendation</option>
                  {nextSkillOptions.map((skill) => (
                    <option key={skill.skillId} value={skill.skillId}>
                      {skill.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-5">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="quick-note">
                Optional quick note
              </label>
              <textarea
                id="quick-note"
                value={quickNote}
                onChange={(event) => setQuickNote(event.target.value)}
                placeholder="Short lesson note. This is designed for quick typing now, and can later support AI summarization or voice capture."
                className="mt-2 min-h-[110px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-900"
              />
            </div>

            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Structured Preview</p>
              <p className="mt-2 text-sm text-slate-700">
                Stages up to <span className="font-medium">{formatLabel(furthestStageReached)}</span> will be inferred automatically.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {(quickPreview?.stages || []).map((stage) => (
                  <span
                    key={stage.stage}
                    className="rounded-full bg-white px-3 py-1 text-sm text-slate-700"
                  >
                    {formatLabel(stage.stage)}: {formatLabel(stage.completionStatus)}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => persistQuickLog('draft')}
                disabled={saving}
                className="flex-1 rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                type="button"
                data-testid="complete-lesson"
                onClick={() => persistQuickLog('completed')}
                disabled={saving || !hasEvidence}
                title={!hasEvidence ? 'Add a note, focus, or outcome first' : undefined}
                className="flex-1 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? 'Saving...' : 'Complete Lesson'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default InstructorQuickLessonLogPage;
